import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createTest, getTest, updateTest } from "@/lib/api";
import { AdminSideNav } from "@/pages/admin/AdminDashboardPage";
import BrandedHeader from "@/components/BrandedHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Brain, ArrowLeft, Plus, Trash2, GripVertical, Loader2, Save, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const emptyQuestion = () => ({
  question_text: "",
  scenario: "",
  options: [
    { label: "A", text: "" },
    { label: "B", text: "" },
    { label: "C", text: "" },
    { label: "D", text: "" },
  ],
  correct_answer: "A",
  explanation: "",
});

export default function AdminTestBuilderPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(testId);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [expandedQ, setExpandedQ] = useState(new Set([0]));

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    time_limit_minutes: 30,
    passing_score: 70,
    questions: [emptyQuestion()],
  });

  useEffect(() => {
    if (isEditing) loadTest();
  }, [isEditing, testId]); // Fixed: added isEditing and testId dependencies, loadTest wrapped below

  const loadTest = useCallback(async () => {
    try {
      const res = await getTest(testId);
      setForm({
        title: res.data.title || "",
        description: res.data.description || "",
        category: res.data.category || "",
        time_limit_minutes: res.data.time_limit_minutes || 30,
        passing_score: res.data.passing_score || 70,
        questions: res.data.questions?.length > 0 ? res.data.questions : [emptyQuestion()],
      });
    } catch (e) { toast.error("Failed to load test"); }
    finally { setLoading(false); }
  }, [testId]);

  const addQuestion = () => {
    const newIdx = form.questions.length;
    setForm({ ...form, questions: [...form.questions, emptyQuestion()] });
    setExpandedQ(new Set([...expandedQ, newIdx]));
  };

  const removeQuestion = (idx) => {
    if (form.questions.length <= 1) { toast.error("Need at least 1 question"); return; }
    const updated = form.questions.filter((_, i) => i !== idx);
    setForm({ ...form, questions: updated });
  };

  const updateQuestion = (idx, field, value) => {
    const updated = [...form.questions];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, questions: updated });
  };

  const updateOption = (qIdx, optIdx, text) => {
    const updated = [...form.questions];
    const opts = [...updated[qIdx].options];
    opts[optIdx] = { ...opts[optIdx], text };
    updated[qIdx] = { ...updated[qIdx], options: opts };
    setForm({ ...form, questions: updated });
  };

  const toggleExpand = (idx) => {
    const s = new Set(expandedQ);
    s.has(idx) ? s.delete(idx) : s.add(idx);
    setExpandedQ(s);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.description.trim()) { toast.error("Description required"); return; }
    
    // Validate questions
    for (let i = 0; i < form.questions.length; i++) {
      const q = form.questions[i];
      if (!q.question_text.trim()) { toast.error(`Q${i + 1}: Question text required`); return; }
      if (q.options.some(o => !o.text.trim())) { toast.error(`Q${i + 1}: All options need text`); return; }
    }

    setSaving(true);
    try {
      if (isEditing) {
        await updateTest(testId, form);
        toast.success("Test updated");
      } else {
        const res = await createTest(form);
        toast.success("Test created");
        navigate(`/admin/tests/${res.data.id}/edit`);
      }
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex">
        <AdminSideNav active="Tests" />
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <AdminSideNav active="Tests" />
      <div className="flex-1 flex flex-col">
        <header className="px-4 sm:px-6 lg:px-8 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/tests')}><ArrowLeft className="w-4 h-4" /></Button>
            <img 
              src="/branding/securado-logo-white.png" 
              alt="Securado" 
              className="h-6 w-auto object-contain"
            />
            <span className="font-display font-semibold">{isEditing ? 'Edit Test' : 'Create Test'}</span>
          </div>
          <Button onClick={handleSave} disabled={saving} className="btn-press" data-testid="save-test-button">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Test
          </Button>
        </header>

        <div className="px-4 sm:px-6 lg:px-8 py-6 flex-1 overflow-auto">
          <div className="max-w-4xl mx-auto">
            {/* Test Info */}
            <Card className="border-border bg-card mb-6">
              <CardHeader className="p-5"><CardTitle className="font-display text-base">Test Information</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5 space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Title *</label>
                  <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Cybersecurity Sales Screening" className="bg-card" data-testid="test-title-input" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Description *</label>
                  <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe what this test evaluates..." rows={3} className="flex w-full rounded-lg border border-input bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 resize-none" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Category</label>
                    <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="Cybersecurity" className="bg-card" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Time Limit (min)</label>
                    <Input type="number" value={form.time_limit_minutes} onChange={e => setForm({ ...form, time_limit_minutes: parseInt(e.target.value) || 30 })} className="bg-card" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Passing Score (%)</label>
                    <Input type="number" value={form.passing_score} onChange={e => setForm({ ...form, passing_score: parseInt(e.target.value) || 70 })} className="bg-card" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Questions */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-semibold text-lg">Questions ({form.questions.length})</h2>
              <Button onClick={addQuestion} variant="secondary" size="sm" data-testid="add-question-button">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Question
              </Button>
            </div>

            <div className="space-y-3" data-testid="questions-list">
              {form.questions.map((q, qIdx) => {
                const isExpanded = expandedQ.has(qIdx);
                return (
                  <Card key={qIdx} className="border-border bg-card">
                    <div className="px-5 py-3 flex items-center justify-between cursor-pointer" onClick={() => toggleExpand(qIdx)}>
                      <div className="flex items-center gap-3 min-w-0">
                        <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <Badge variant="secondary" className="text-xs flex-shrink-0">Q{qIdx + 1}</Badge>
                        <span className="text-sm truncate">{q.question_text || '(empty question)'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">Answer: {q.correct_answer}</Badge>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[hsl(var(--destructive))]" onClick={e => { e.stopPropagation(); removeQuestion(qIdx); }}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </div>
                    {isExpanded && (
                      <CardContent className="px-5 pb-5 pt-0 space-y-4">
                        <Separator />
                        <div>
                          <label className="text-sm font-medium mb-1.5 block">Scenario Context (optional)</label>
                          <textarea value={q.scenario} onChange={e => updateQuestion(qIdx, 'scenario', e.target.value)} placeholder="Set the scene for this question..." rows={2} className="flex w-full rounded-lg border border-input bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 resize-none" />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1.5 block">Question *</label>
                          <textarea value={q.question_text} onChange={e => updateQuestion(qIdx, 'question_text', e.target.value)} placeholder="What is the best response?" rows={2} className="flex w-full rounded-lg border border-input bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 resize-none" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Options</label>
                          {q.options.map((opt, oIdx) => (
                            <div key={oIdx} className="flex items-start gap-2">
                              <Badge variant={q.correct_answer === opt.label ? "default" : "secondary"} className={`mt-1 text-xs cursor-pointer ${q.correct_answer === opt.label ? 'bg-[hsl(var(--success))] text-white' : ''}`} onClick={() => updateQuestion(qIdx, 'correct_answer', opt.label)}>
                                {opt.label}
                              </Badge>
                              <textarea value={opt.text} onChange={e => updateOption(qIdx, oIdx, e.target.value)} placeholder={`Option ${opt.label}...`} rows={1} className="flex-1 rounded-lg border border-input bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 resize-none" />
                            </div>
                          ))}
                          <p className="text-xs text-muted-foreground">Click A/B/C/D badge to set correct answer</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1.5 block">Explanation (shown in review)</label>
                          <Input value={q.explanation} onChange={e => updateQuestion(qIdx, 'explanation', e.target.value)} placeholder="Why this answer is correct..." className="bg-card" />
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>

            <div className="mt-6 text-center">
              <Button onClick={addQuestion} variant="outline" className="btn-press">
                <Plus className="w-4 h-4 mr-2" /> Add Another Question
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
